import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Failed, PaneSkeleton } from "./DiscountParts";
import { ABSENT, messageFor, money } from "./format";
import {
  actualText, canDismiss, canSelect, CHANGES_KEY, correctDrift, correctionRefusal, dismissDrift, dismissRefusal, DRIFT_KEY, DRIFT_SHOWS, driftQuery,
  gapText, lockText, MAX_DISMISS, productName, searchForChange, searchForDriftShow, searchForSubscriberPane, showCount, subscriberParams, SUBSCRIBERS_COPY,
  SUBSCRIBERS_PAGE_SIZE, type DriftRow, type SubscriberMarket,
} from "./subscribers";

const T = SUBSCRIBERS_COPY;
const C = T.drift;
const D = C.dismiss;
const R = C.correct;
const L = C.lock;
type Note = { key: string; tone: "ok" | "err"; text: string };
const STATUS_TONE: Record<DriftRow["status"], string> = { pays_more: "text-ks-warn", pays_less: "text-ks-ink", unchecked: "text-ks-grey-1", on_price: "text-ks-pass" };

/** The Drift pane: every active subscription of the store against the committed shelf price less its plan discount, as C2
 * judged it. By default the rows that need a person: who pays more than a new subscriber first, then who pays less, then the
 * ones that could not be checked, each with its reason. C1 v1.43 (c1-drift-dismiss): a drifted row can be selected and the
 * selection dismissed with one reason (C2 v1.44); a dismissed row leaves the default view and the status views, reads under
 * Dismissed with its reason, and is counted apart. Rows on the price or unchecked cannot be selected. The status or alert of a
 * write is the pane's, not the table's, so it outlives the table when the last drifted row leaves (round 2, R2-2). C1 v1.46
 * (c1-subscription-exceptions): a row an open change holds is locked and opens its change; a row holding a hardship exception
 * reads under Excepted; the selection can be corrected by planned changes, one per SKU and direction (C2 v1.47). */
export function SubscriberDriftPane({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const params = subscriberParams(search);
  const tableKey = `${params.market}|${params.show}|${params.page}`;
  const [note, setNote] = useState<Note | null>(null);
  const page = useQuery(driftQuery(params.market, params.show, params.page));
  // The summary, the read and the filter counts are the same in every answer of a store, so while a filter's or a page's own
  // read is on its way they come from the store's default read (the tab's Drift count holds it): the filter buttons stay on
  // screen, keyboard focus stays on the pressed one, and only the table waits.
  const base = useQuery(driftQuery(params.market, "attention", 0));
  const heading = <div><h3 className="text-base font-semibold text-ks-ink">{C.title}</h3><p className="text-sm text-ks-grey-1">{C.note}</p></div>;
  const failed = page.error ? <Failed sentence={`${C.error} ${messageFor(page.error, areaName)}`} onRetry={() => void page.refetch()} /> : null;
  const d = page.data ?? base.data;
  if (!d) return <div className="flex flex-col gap-3">{heading}{failed ?? <PaneSkeleton />}</div>;
  const region = T.regionLabel[params.region];
  if (d.read === null) {
    return <div className="flex flex-col gap-3">{heading}<Card><CardContent className="py-10 text-center"><p className="text-lg font-semibold text-ks-ink">{T.neverRead.title}</p><p className="text-ks-grey-1">{T.neverRead.body(region)}</p></CardContent></Card></div>;
  }
  const s = d.summary;
  const notices = (
    <>
      {d.read.stale && <Alert role="status"><AlertDescription>{T.stale(d.read)}</AlertDescription></Alert>}
      {d.read.refused > 0 && <Alert role="status"><AlertDescription>{T.refused(d.read.refused)}</AlertDescription></Alert>}
    </>
  );
  if (s.subscriptions === 0) {
    return <div className="flex flex-col gap-3">{heading}{notices}<Card><CardContent className="py-10 text-center"><p className="text-lg font-semibold text-ks-ink">{C.noneTitle}</p><p className="text-ks-grey-1">{C.noneBody(region)}</p><p className="text-xs text-ks-grey-1">{T.read(d.read)}</p></CardContent></Card></div>;
  }
  const calm = params.show === "attention" && d.page.total === 0;
  const excepted = s.excepted ?? 0;
  return (
    <div className="flex flex-col gap-3">
      {heading}
      {notices}
      <p className="text-sm text-ks-ink">{C.summary(s)}{s.placed_by_variant > 0 ? ` ${C.byVariant(s.placed_by_variant)}` : ""}</p>
      <div role="group" aria-label={C.showLabel} className="flex flex-wrap gap-1">
        {DRIFT_SHOWS.map((key) => (
          <Button key={key} size="sm" variant={key === params.show ? "default" : "outline"} aria-pressed={key === params.show} data-nonmutating onClick={() => go(searchForDriftShow(search, key))}>
            {C.show[key]} ({showCount(s, key)})
          </Button>
        ))}
      </div>
      {note && note.key === tableKey && <p role={note.tone === "err" ? "alert" : "status"} className={`text-sm ${note.tone === "err" ? "text-ks-danger" : "text-ks-grey-1"}`}>{note.text}</p>}
      {!page.data
        ? failed ?? <PaneSkeleton />
        : calm
        ? <Card><CardContent className="py-10 text-center"><p className="text-lg font-semibold text-ks-ink">{excepted > 0 ? C.allSettled : s.dismissed > 0 ? C.allDismissed : C.allOnPrice}</p><p className="text-ks-grey-1">{C.allOnPriceBody(s.unchecked, s.dismissed, excepted)}</p></CardContent></Card>
        : <DriftTable key={tableKey} rows={d.rows} market={params.market} areaName={areaName} onNote={(n) => setNote(n && { key: tableKey, ...n })}
            onOpenChange={(id) => go(searchForChange(searchForSubscriberPane(search, "changes"), id))}
            page={{ pageIndex: params.page, pageSize: SUBSCRIBERS_PAGE_SIZE, rowCount: d.page.total }} onPageChange={(next) => go({ ...search, pp_spage: next || undefined })} />}
      <p className="text-xs text-ks-grey-1">{T.read(d.read)}. {C.footnote}</p>
    </div>
  );
}

/** The table with its selection and its two writes, the correction and the dismissal (one at a time). The selection is the
 * table's own and starts empty on every market, filter and page (the key above): a row picked on one page is never acted on
 * from another unseen. */
function DriftTable({ rows, market, areaName, page, onPageChange, onNote, onOpenChange }: {
  rows: DriftRow[]; market: SubscriberMarket; areaName: string; page: { pageIndex: number; pageSize: number; rowCount: number }; onPageChange: (next: number) => void;
  onNote: (note: Omit<Note, "key"> | null) => void; onOpenChange: (changeId: number) => void;
}) {
  const client = useQueryClient();
  const [picked, setPicked] = useState<ReadonlySet<number>>(new Set());
  const [open, setOpen] = useState<"correct" | "dismiss" | null>(null);
  const [reason, setReason] = useState("");
  // a row the re-read no longer offers (held by a change, moved, gone) leaves the selection
  const offered = new Set(rows.filter(canSelect).map((r) => r.subscription_id));
  useEffect(() => { setPicked((prev) => new Set([...prev].filter((id) => offered.has(id)))); }, [rows]);   // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = rows.filter((r) => picked.has(r.subscription_id));
  const ids = chosen.map((r) => r.subscription_id);
  // a dismissed row may be corrected, never dismissed again (C2 refuses `already_dismissed`): Dismiss is offered on none
  const dismissable = chosen.every(canDismiss);
  const done = () => { setOpen(null); setReason(""); setPicked(new Set()); };
  const dismiss = useMutation({
    mutationFn: (text: string) => dismissDrift(market, ids, text),
    onSuccess: (answer) => {
      done();
      onNote({ tone: "ok", text: D.done(answer.dismissed.length) });
    },
    onError: (error) => onNote({ tone: "err", text: dismissRefusal(error, areaName) }),
    // the report is the truth either way: every drift read of every market is read again (the tab's count among them)
    onSettled: () => { void client.invalidateQueries({ queryKey: DRIFT_KEY }); },
  });
  // the correction: C2 plans one change per SKU and direction and answers them; the rows are held now, so every drift read is read
  // again, and so is every changes read, where the planned changes now show
  const correction = useMutation({
    mutationFn: (selected: number[]) => correctDrift(market, selected),
    onSuccess: (answer, selected) => {
      done();
      onNote({ tone: "ok", text: R.done(answer.planned.length, selected.length) });
    },
    onError: (error) => onNote({ tone: "err", text: correctionRefusal(error, areaName) }),
    onSettled: () => { void client.invalidateQueries({ queryKey: DRIFT_KEY }); void client.invalidateQueries({ queryKey: CHANGES_KEY }); },
  });
  const busy = dismiss.isPending || correction.isPending;
  const toggle = (id: number) => setPicked((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const submit = () => {
    if (!reason.trim()) { onNote({ tone: "err", text: D.empty }); return; }
    if (ids.length > MAX_DISMISS) { onNote({ tone: "err", text: D.tooMany(MAX_DISMISS) }); return; }
    onNote(null); dismiss.mutate(reason);
  };
  const plan = () => {
    if (ids.length > MAX_DISMISS) { onNote({ tone: "err", text: D.tooMany(MAX_DISMISS) }); return; }
    onNote(null); correction.mutate(ids);
  };
  const columns: ColumnDef<DriftRow, unknown>[] = [
    { id: "pick", header: () => <span className="sr-only">{D.column}</span>, cell: ({ row: r }) => <PickCell row={r.original} picked={picked.has(r.original.subscription_id)} busy={busy} onToggle={() => toggle(r.original.subscription_id)} /> },
    { id: "product", header: C.columns[0], cell: ({ row: r }) => <ProductCell row={r.original} /> },
    { id: "subscription", header: C.columns[1], cell: ({ row: r }) => <SubscriptionCell row={r.original} /> },
    { id: "actual", header: C.columns[2], meta: { align: "right" }, cell: ({ row: r }) => <span className="tabular-nums text-ks-ink">{actualText(r.original)}</span> },
    { id: "expected", header: C.columns[3], meta: { align: "right" }, cell: ({ row: r }) => <span className="tabular-nums text-ks-grey-1">{r.original.expected_price == null ? ABSENT : money(r.original.expected_price, r.original.currency)}</span> },
    { id: "gap", header: C.columns[4], meta: { align: "right" }, cell: ({ row: r }) => <span className={`tabular-nums ${r.original.status === "pays_more" ? "font-semibold text-ks-warn" : "text-ks-ink"}`}>{r.original.status === "on_price" ? ABSENT : gapText(r.original)}</span> },
    { id: "note", header: C.columns[5], cell: ({ row: r }) => <NoteCell row={r.original} onOpenChange={onOpenChange} /> },
  ];
  return (
    <div className="flex flex-col gap-2">
      {ids.length > 0 && (
        <div role="region" aria-label={D.selected(ids.length)} className="flex flex-wrap items-center gap-2 rounded-md border border-ks-line bg-ks-purple-4 p-2" data-drift-selection>
          <span className="text-sm text-ks-ink">{D.selected(ids.length)}</span>
          {!open && <Button size="sm" disabled={busy} onClick={() => { setOpen("correct"); onNote(null); }} data-drift-correct>{R.button(ids.length)}</Button>}
          {!open && dismissable && <Button size="sm" variant="outline" disabled={busy} onClick={() => { setOpen("dismiss"); onNote(null); }}>{D.button(ids.length)}</Button>}
          <Button size="sm" variant="outline" data-nonmutating disabled={busy} onClick={() => { setPicked(new Set()); setOpen(null); setReason(""); }}>{D.clear}</Button>
          {!open && !dismissable && <p className="basis-full text-xs text-ks-grey-1" data-drift-no-dismiss>{R.noDismiss}</p>}
          {open === "correct" && (
            <div className="flex basis-full flex-col gap-1" data-drift-correct-form>
              <p className="text-sm text-ks-ink">{R.body}</p>
              <div className="flex gap-1">
                <Button size="sm" disabled={busy} onClick={plan}>{correction.isPending ? R.pending : R.save}</Button>
                <Button size="sm" variant="outline" data-nonmutating disabled={busy} onClick={() => setOpen(null)}>{R.cancel}</Button>
              </div>
            </div>
          )}
          {open === "dismiss" && (
            <div className="flex basis-full flex-col gap-1" data-drift-dismiss-form>
              <Label htmlFor="pp-drift-dismiss-reason" className="text-sm">{D.label}</Label>
              <Textarea id="pp-drift-dismiss-reason" value={reason} maxLength={500} rows={2} placeholder={D.placeholder} onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") { setOpen(null); setReason(""); } }} />
              <p className="text-xs text-ks-grey-1">{D.hint}</p>
              <div className="flex gap-1">
                <Button size="sm" disabled={busy} onClick={submit}>{dismiss.isPending ? D.pending : D.save}</Button>
                <Button size="sm" variant="outline" data-nonmutating disabled={busy} onClick={() => { setOpen(null); setReason(""); }}>{D.cancel}</Button>
              </div>
            </div>
          )}
        </div>
      )}
      <DataTable columns={columns} rows={rows} page={page} onPageChange={onPageChange} caption={C.title} emptyMessage={C.noMatch} density="compact" />
    </div>
  );
}

/** The tick box of a row a person may select; a locked row says why it has none, for a screen reader. */
function PickCell({ row, picked, busy, onToggle }: { row: DriftRow; picked: boolean; busy: boolean; onToggle: () => void }) {
  if (canSelect(row)) {
    return <Checkbox aria-label={D.select(row.subscription_id)} checked={picked} disabled={busy} onCheckedChange={onToggle} data-nonmutating />;
  }
  const why = row.lock ? (row.lock.exception_left !== null ? L.excepted : L.held) : D.locked[row.status as "unchecked" | "on_price"];
  return <span className="sr-only" data-drift-locked={row.subscription_id}>{why}</span>;
}

/** The row's status, its dismissal, and the change that holds it with a link that opens it in Price changes. */
function NoteCell({ row, onOpenChange }: { row: DriftRow; onOpenChange: (changeId: number) => void }) {
  const status = <span className={`text-sm ${STATUS_TONE[row.status]}`}>{row.status === "unchecked" ? C.unchecked(row.unchecked_reason, row.rate) : C.status[row.status]}</span>;
  const lock = row.lock ?? null;
  if (!row.dismissed && !lock) return status;
  return (
    <div>
      {status}
      {row.dismissed && <div className="text-xs text-ks-grey-1" data-drift-dismissed={row.subscription_id}>{D.note(row.dismissed)}</div>}
      {lock && (
        <div className="text-xs text-ks-grey-1" data-drift-lock={lock.change_id}>
          {lockText(lock)} · <Button variant="link" size="sm" className="h-auto p-0 text-xs" data-nonmutating onClick={() => onOpenChange(lock.change_id)}>{L.open}</Button>
        </div>
      )}
    </div>
  );
}

function ProductCell({ row }: { row: DriftRow }) {
  return (
    <div>
      <span className="font-semibold text-ks-ink">{productName(row)}</span>
      <div className="text-xs text-ks-grey-1">{row.xero_sku ?? C.noSku}{row.sku_source === "variant" ? ` · ${C.fromVariant}` : ""}</div>
    </div>
  );
}
function SubscriptionCell({ row }: { row: DriftRow }) {
  const facts = [row.quantity > 1 ? C.quantity(row.quantity) : null, row.next_charge_on ? C.nextCharge(row.next_charge_on) : null].filter(Boolean).join(" · ");
  return <div><span className="tabular-nums text-sm text-ks-ink">{C.subscription(row.subscription_id)}</span>{facts && <div className="text-xs text-ks-grey-1">{facts}</div>}</div>;
}
